import React, { useState, useEffect } from "react";
import { ArrowUpRight, CalendarDays } from "lucide-react";
// Base URL for Google Cloud Storage bucket
const GCS_BASE_URL = "https://storage.googleapis.com/wacofirefightersbenefit";
const CDN_BASE_URL = "https://storage.googleapis.com/wacofirefightersbenefit";

const Events = () => {
    const [eventsData, setEventsData] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadEventsData();
    }, []);

    const loadEventsData = async () => {
        try {
            const response = await fetch(`${CDN_BASE_URL}/data/events.json?t=${Date.now()}`);
            if (response.ok) {
                const data = await response.json();
                setEventsData(data);
            } else {
                console.warn('Failed to load events data, using empty array');
                setEventsData([]);
            }
        } catch (err) {
            console.error('Error loading events data:', err);
            setEventsData([]);
        } finally {
            setLoading(false);
        }
    };
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Separate events into upcoming and past
    const upcomingEvents = eventsData.filter(event => {
        const eventDate = new Date(event.date);
        return eventDate >= today;
    }).sort((a, b) => new Date(a.date) - new Date(b.date));

    const pastEvents = eventsData.filter(event => {
        const eventDate = new Date(event.date);
        return eventDate < today;
    }).sort((a, b) => new Date(b.date) - new Date(a.date)); // Most recent first

    // Helper function to render a ticket image (supports fan layout via transform + wrapperStyle)
    const renderTicket = (filename, event, index, transform, wrapperStyle = {}, allowDownload = true) => {
        const fileUrl = `${GCS_BASE_URL}/${filename}`;
        const imageEl = (
            <img
                width={300}
                style={{
                    borderRadius: '10px',
                    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
                }}
                src={fileUrl}
                alt={`${event.title} Ticket`}
            />
        );
        const baseStyle = {
            transform,
            transformOrigin: '50% 100%',
            display: 'block',
            position: 'absolute',
            left: '50%',
            top: 0,
            ...wrapperStyle,
        };
        if (!allowDownload) {
            return (
                <div key={index} style={{ ...baseStyle, cursor: 'default' }}>
                    {imageEl}
                </div>
            );
        }
        return (
            <a
                key={index}
                href={fileUrl}
                download
                style={{
                    ...baseStyle,
                    cursor: 'pointer',
                }}
                title="Click to download"
            >
                {imageEl}
            </a>
        );
    };

    const renderEvent = (event, isPast = false) => {
        return (
            <div
                key={event.id}
                style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr',
                    gridAutoRows: 'auto',
                    gap: '24px',
                    alignContent: 'start',
                    paddingBottom: '48px',
                    borderBottom: '1px solid rgba(0,0,0,0.08)',
                    marginBottom: '48px',
                }}
            >
                <h1 className="text-4xl font-bold text-center mb-4">{event.title}</h1>
                <p className="text-lg text-center flex flex-row items-center text-center justify-center">{event.displayDate} {event.photosUrl && (<> • <a href={event.photosUrl} target="_blank"
                        rel="noopener noreferrer" className="ml-1 underline text-blue-500 flex flex-row items-center text-center justify-center"> View Images <ArrowUpRight className="h-4 w-4"/> </a></>)}</p>
                {event.address && <p className="text-lg text-center">{event.location} • {event.address}</p>}

                {/* Show buttons for all upcoming events */}
                {!isPast && (
                    <>
                        <button
                            className="items-center appearance-none bg-[#FCFCFD] rounded-[16px] border-0 shadow-[rgba(45,35,66,0.4)_0_2px_4px,rgba(45,35,66,0.3)_0_7px_13px_-3px,#D6D6E7_0_-3px_3px_inset] box-border text-[#36395A] cursor-pointer inline-flex h-[48px] w-[150px] justify-center leading-none list-none overflow-hidden px-[16px] relative text-left no-underline transition-[box-shadow,transform] duration-150 select-none whitespace-nowrap will-change-[box-shadow,transform] text-[18px] m-[5px] hover:shadow-[rgba(45,35,66,0.2)_0_4px_8px,rgba(45,35,66,0.2)_0_7px_13px_-3px,#D6D6E7_0_-3px_3px_inset] hover:-translate-y-[2px] active:shadow-[#D6D6E7_0_3px_7px_inset] active:translate-y-[2px] font-bold"
                            style={!event.printTicketUrl ? { cursor: 'not-allowed' } : { cursor: 'pointer' }}
                            disabled={!event.printTicketUrl}
                        >
                            {event.printTicketUrl ? (
                                <a className="no-underline text-[#5a5a5a]" href={event.printTicketUrl} download>
                                    <b>Print Ticket</b>
                                </a>
                            ) : (
                                <b>Print Ticket</b>
                            )}
                        </button>
                        <button
                            className={event.onlineTicketUrl ? "items-center appearance-none bg-[#e4e4f1] rounded-[16px] border-0 shadow-[rgba(45,35,66,0.7)_0_2px_4px,rgba(45,35,66,0.4)_0_7px_13px_-3px,#8787a0_0_-3px_4px_inset] box-border text-[#36395A] cursor-pointer inline-flex h-[48px] w-[150px] justify-center leading-none list-none overflow-hidden px-[16px] relative text-left no-underline transition-[box-shadow,transform] duration-150 select-none whitespace-nowrap will-change-[box-shadow,transform] text-[18px] m-[5px] hover:shadow-[rgba(45,35,66,0.2)_0_4px_8px,rgba(45,35,66,0.2)_0_7px_13px_-3px,#8787a0_0_-3px_3px_inset] hover:-translate-y-[2px] active:shadow-[#D6D6E7_0_3px_7px_inset] active:translate-y-[2px] font-bold" : "inline-flex items-center justify-center appearance-none bg-[#FCFCFD] rounded-2xl border-0 shadow-[rgba(45,35,66,0.4)_0_2px_4px,rgba(45,35,66,0.3)_0_7px_13px_-3px,#D6D6E7_0_-3px_3px_inset] box-border text-[#36395A] h-12 w-[150px] leading-none list-none overflow-hidden px-4 relative text-left no-underline transition-[box-shadow,transform] duration-150 select-none touch-manipulation whitespace-nowrap text-lg m-[5px] active:translate-y-[2px] hover:translate-y-[2px]"}
                            disabled={!event.onlineTicketUrl}
                            style={!event.onlineTicketUrl ? { cursor: 'not-allowed' } : { cursor: 'pointer' }}
                        >
                            {event.onlineTicketUrl ? (
                                <a className="no-underline text-[#5a5a5a]" href={event.onlineTicketUrl}>
                                    <b>Online Tickets</b>
                                </a>
                            ) : (
                                <b>Online Tickets</b>
                            )}
                        </button>
                    </>
                )}


                {/* Fanned, overlapping ticket images (card-hand style) */}
                {event.images.length > 0 && (() => {
                    const n = event.images.length;
                    const spreadPx = 56;
                    const spreadDeg = 12;
                    const baseH = 320;
                    return (
                        <div
                            style={{
                                position: 'relative',
                                width: '100%',
                                height: baseH,
                                marginTop: '24px',
                                marginBottom: '160px',
                            }}
                        >
                            {event.images.map((image, index) => {
                                const t = n > 1 ? (index - (n - 1) / 2) : 0;
                                const tx = t * spreadPx;
                                const rot = t * spreadDeg;
                                const transform = `translate(-50%, 0) translateX(${tx}px) rotate(${rot}deg)`;
                                return renderTicket(
                                    image,
                                    event,
                                    index,
                                    transform,
                                    { zIndex: index },
                                    !isPast
                                );
                            })}
                        </div>
                    );
                })()}
            </div>
        );
    };

    if (loading) {
        return (
            <div className="pt-[100px]">
                <p className="text-lg text-center mb-2">Loading events...</p>
            </div>
        );
    }

    return (
        <div
            className="pt-[100px] pb-[500px]"
            style={{
                display: 'grid',
                gridTemplateColumns: '1fr',
                gridAutoRows: 'auto',
                gap: '0',
                maxWidth: '100%',
            }}
        >
            {/* Render upcoming events — each event is one grid row with its own space */}
            {upcomingEvents.length > 0 ? (
                upcomingEvents.map(event => renderEvent(event, false))
            ) : (
                <div className="p-10 my-32 bg-gradient-to-b from-white via-[#e4e4f1] to-white text-[#36395A] border border-[#ceceda] md:w-96 mx-auto flex text-center flex-col rounded-[30px] justify-center">
                    <CalendarDays className="h-10 w-10 self-center text-[#5a5a5a] mb-2" strokeWidth={1.5}/>
                    <h1 className="text-xl text-center text-[#5a5a5a] select-none" style={{ gridColumn: '1' }}>No Upcoming Events </h1>
                <p className="text-lg text-center text-[#5a5a5a] select-none" style={{ gridColumn: '1' }}>
                    Check back here for new events.
                </p>
                </div>
            )}

            {/* Divider between upcoming and past events */}
            {pastEvents.length > 0 && (
                <>
                    <hr style={{ width: '100%', marginTop: '24px', marginBottom: '24px', gridColumn: '1' }} />
                    <p className="text-lg text-center mb-2 mt-[24px]" style={{ gridColumn: '1' }}>Past Events</p>
                </>
            )}

            {/* Render past events */}
            {pastEvents.map(event => renderEvent(event, true))}
        </div>
    );
};

export default Events;
